import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAllBranches } from '../../../contexts/BranchesContext';
import { branchLabel, groupByManager } from '../components/managersAgents/agentManagerUtils';

const API = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/tradeDelay`;

// Agent→manager rows (+ portal users for the manager picker) and their
// add / edit / remove calls. State updates are skipped after unmount.
const useAgentManagerRows = (execute) => {
  const mountedRef = useRef(true);
  const [assignments, setAssignments] = useState({ canEdit: false, rows: [], users: [] });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await execute('GET', `${API}/agentManagers`);
      if (mountedRef.current) setAssignments({ canEdit: !!res?.canEdit, rows: res?.rows || [], users: res?.users || [] });
    } catch {
      if (mountedRef.current) setLoadError('שגיאה בטעינת המנהלים');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [execute]);

  useEffect(() => {
    mountedRef.current = true;
    load();
    return () => {
      mountedRef.current = false;
    };
  }, [load]);

  // editId set = update that one row; otherwise create (several agents at once).
  const save = async (payload, editId) => {
    if (editId) {
      const { agents, ...rest } = payload;
      await execute('PUT', `${API}/agentManagers/${editId}`, { ...rest, ...agents[0] });
    } else {
      await execute('POST', `${API}/agentManagers`, payload);
    }
    await load();
  };

  const remove = async (rowId) => {
    await execute('DELETE', `${API}/agentManagers/${rowId}`);
    await load();
  };

  return { ...assignments, loading, loadError, save, remove };
};

// Priority agents (active Neopharm agents with an email) — picker options and
// the email shown next to each assigned agent.
const useAgentOptions = (execute) => {
  const [agentOptions, setAgentOptions] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(true);
  const [agentsError, setAgentsError] = useState('');

  useEffect(() => {
    let cancelled = false;
    execute('GET', `${API}/agents`)
      .then((res) => !cancelled && setAgentOptions(res || []))
      .catch(() => !cancelled && setAgentsError('שגיאה בטעינת רשימת הסוכנים מפריוריטי'))
      .finally(() => !cancelled && setAgentsLoading(false));
    return () => {
      cancelled = true;
    };
  }, [execute]);

  const agentEmails = useMemo(
    () => (agentsLoading || agentsError ? null : new Map(agentOptions.map((a) => [a.agentCode, a.email]))),
    [agentOptions, agentsLoading, agentsError],
  );

  return { agentOptions, agentsLoading, agentsError, agentEmails };
};

// Branch names from the Priority BRANCHES list (BranchesContext).
const useBranchLookup = () => {
  const { allBranches = [] } = useAllBranches() || {};
  const branchNames = useMemo(() => new Map(allBranches.map((b) => [b.BRANCHNAME, b.BRANCHDES || ''])), [allBranches]);
  const branchOptions = useMemo(
    () => allBranches.map((b) => ({ code: b.BRANCHNAME, label: branchLabel(b.BRANCHNAME, branchNames) })),
    [allBranches, branchNames],
  );
  return { branchNames, branchOptions };
};

const useAgentManagers = (execute) => {
  const rows = useAgentManagerRows(execute);
  const agents = useAgentOptions(execute);
  const branches = useBranchLookup();
  const managers = useMemo(() => groupByManager(rows.rows), [rows.rows]);
  return { ...rows, ...agents, ...branches, managers };
};

export default useAgentManagers;
